import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {adjustToTimeZone, Role, RoleEntity, roleSelector, useGetRoleQuery} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {RoleForm, RoleFormDefaultValues, RoleFormValues} from "../../components/form/RoleForm/RoleForm";
import {useRoleMutation} from "../../hooks/useRoleMutation";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: RoleFormDefaultValues
}

export const RoleFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <RoleFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const toFormDefaultsFromRole = (role: RoleEntity | undefined): RoleFormDefaultValues => {
  return {
    name: role?.name,
    description: role?.description,
  };
};

export const RoleFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectRole = useMemo(() => {
    return roleSelector()
  }, [])

  const {currRole, isLoading} = useGetRoleQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currRole: selectRole(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: RoleFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromRole(currRole);
  }, [currRole, formDefaults]);

  const isEdit = useMemo(() => {
    return currRole && !copy
  }, [currRole, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.role.form.title.edit", "Edit Role", {ns: "common"});
    } else {
      return t("page.entity.role.form.title.new", "New Role", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createRole,
    updateRole,
    isCreating,
    isUpdating,
  } = useRoleMutation()

  const onRoleSubmit = useCallback((formValues: RoleFormValues) => {
    const patch: Partial<Role> = {
      name: formValues.name,
      description: formValues.description,
    }

    if (isEdit) {
      const role: Partial<Role> = {
        ...currRole.asTypeValue(),
        ...patch,
        lastModifiedDttm: adjustToTimeZone().getTime(),
      };
      updateRole(role, () => navigate(-1))
    } else {
      const role: Partial<Role> = {
        ...patch,
      };
      createRole(role, (payload) => navigate(`/roles/profile?id=${payload.id}`, {replace: true}))
    }
  }, [currRole, isEdit, createRole, updateRole, navigate]);

  return (
    <ProtectedPage permissions={[isEdit ? "role_edit" : "role_create"]} isLoading={isLoading} onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <RoleForm
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onRoleSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

